// src/hooks/useDeviceOperations.js
import { useCallback } from 'react';
import config from '../config';
import logger from '../utils/logger';
import {
  getDeviceStateCache,
  setDeviceStateCache,
  invalidateDeviceStateCache as invalidateDeviceStateCacheUtil,
  getInstallationSyncCache,
  setInstallationSyncCache,
  invalidateInstallationSyncCache,
} from '../utils/cacheManager';

/**
 * Custom hook for device data fetching operations
 * 
 * Provides functions to:
 * - Fetch device list
 * - Fetch device state (write mode - invalidates cache)
 * - Fetch device state read-only (with 30s cache)
 * - Invalidate device state cache
 * - Manage installation-wide sync cache
 * 
 * @param {Function} authenticatedFetch - Authenticated fetch wrapper from AuthContext
 * @returns {Object} Device operation functions
 */
export const useDeviceOperations = (authenticatedFetch) => {
  // Fetch device list for installation
  const fetchDevices = useCallback(async () => {
    logger.log('Fetching device list...');
    try {
      const response = await authenticatedFetch(`${config.apiEndpoint}/devices`, {
        headers: { 'x-api-key': config.apiKey },
      });
      logger.log('Fetch call completed. Response received:', response);
      logger.log(
        `Response OK: ${response.ok}, Response Status: ${response.status}`
      );
      if (!response.ok) {
        if (response.status === 403) {
          throw new Error(
            'An app configuration issue is preventing this app from loading successfully. Please reach out to an admin for support.'
          );
        }
        throw new Error(`API call to fetch devices failed: ${response.status}`);
      }
      return await response.json();
    } catch (error) {
      logger.error('--- CATCH BLOCK TRIGGERED in fetchDevices ---', error);
      throw error;
    }
  }, [authenticatedFetch]);

  // Fetch device state (write mode - no cache, for editing)
  const fetchDeviceState = useCallback(async (deviceId) => {
    if (!deviceId) return null;
    logger.log(`Fetching state for device ${deviceId}...`);
    try {
      const response = await authenticatedFetch(
        `${config.apiEndpoint}/device-state?deviceId=${deviceId}`,
        { headers: { 'x-api-key': config.apiKey } }
      );
      if (!response.ok) {
        throw new Error(
          `API call to fetch device state failed: ${response.status}`
        );
      }
      return await response.json();
    } catch (error) {
      logger.error('Failed to fetch device state:', error);
      return null;
    }
  }, [authenticatedFetch]);

  // Fetch device state read-only (with 30-second cache)
  const fetchDeviceStateReadOnly = useCallback(async (deviceId) => {
    if (!deviceId) return null;

    // Check cache
    const cachedData = getDeviceStateCache(deviceId);
    if (cachedData) {
      return cachedData;
    }

    // Cache miss or expired - fetch from API
    logger.log(`Fetching read-only state for device ${deviceId} from API...`);
    try {
      const response = await authenticatedFetch(
        `${config.apiEndpoint}/device-state-readonly?deviceId=${deviceId}`,
        { headers: { 'x-api-key': config.apiKey } }
      );
      if (!response.ok) {
        throw new Error(
          `API call to fetch read-only device state failed: ${response.status}`
        );
      }
      const data = await response.json();

      // Store in cache
      setDeviceStateCache(deviceId, data);

      return data;
    } catch (error) {
      logger.error('Failed to fetch read-only device state:', error);
      return null;
    }
  }, [authenticatedFetch]);

  // Invalidate device state cache (wrapper for cache utility)
  const invalidateDeviceStateCache = useCallback((deviceId) => {
    invalidateDeviceStateCacheUtil(deviceId);
  }, []);

  return {
    fetchDevices,
    fetchDeviceState,
    fetchDeviceStateReadOnly,
    invalidateDeviceStateCache,
    getInstallationSyncCache,
    setInstallationSyncCache,
    invalidateInstallationSyncCache,
  };
};
